// Shared presentation helpers for the Hardware list and device page.
import React from 'react';
import L from 'leaflet';
import { Box } from '@mui/material';
import {
  Router as RouterIcon,
  Sensors as SensorsIcon,
  Hub as HubIcon,
  Memory as MemoryIcon,
  Cloud as CloudIcon,
  WaterDrop as WaterDropIcon,
  Air as AirIcon,
  DevicesOther as DevicesOtherIcon,
  BatteryFull as BatteryFullIcon,
  Battery80 as Battery80Icon,
  Battery50 as Battery50Icon,
  Battery20 as Battery20Icon,
  BatteryAlert as BatteryAlertIcon,
  SignalCellular4Bar as Signal4Icon,
  SignalCellular3Bar as Signal3Icon,
  SignalCellular2Bar as Signal2Icon,
  SignalCellular1Bar as Signal1Icon,
  SignalCellularOff as SignalOffIcon,
} from '@mui/icons-material';
import type { Logger } from '../../services';
import { parseUtc } from '../../utils/datetime';

export type StatusKey = 'UP' | 'SUSPECT' | 'DOWN' | 'UNKNOWN';

export const statusMeta = (
  trackStatus: string | undefined,
): { key: StatusKey; label: string; color: 'success' | 'warning' | 'error' | 'default' } => {
  switch ((trackStatus || '').toUpperCase()) {
    case 'UP':
      return { key: 'UP', label: 'Online', color: 'success' };
    case 'SUSPECT':
      return { key: 'SUSPECT', label: 'Suspect', color: 'warning' };
    case 'DOWN':
      return { key: 'DOWN', label: 'Offline', color: 'error' };
    default:
      return { key: 'UNKNOWN', label: 'Unknown', color: 'default' };
  }
};

// Map pin coloured by the logger's track status (same palette as the rows).
export const pinIconFor = (fill: string): L.Icon =>
  new L.Icon({
    iconUrl:
      'data:image/svg+xml;base64,' +
      btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
      <path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="${fill}" stroke="#FFFFFF" stroke-width="1.5"/>
      <circle cx="12.5" cy="12.5" r="6" fill="white"/>
    </svg>
  `),
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });

// LocationTime is stored in UTC but serialised without a zone marker, so
// tag it as UTC before converting to the viewer's local time.
export const utcToLocalString = (iso: string): string =>
  new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`).toLocaleString();

export const typeIcon = (typeName?: string, fontSize: 'small' | 'medium' | 'large' = 'small') => {
  switch (typeName) {
    case 'Agent':
      return <RouterIcon fontSize={fontSize} />;
    case 'Sensor':
    case 'Digit Sensor':
      return <SensorsIcon fontSize={fontSize} />;
    case 'Conx-1':
      return <MemoryIcon fontSize={fontSize} />;
    case 'Gateway':
    case 'Router':
      return <HubIcon fontSize={fontSize} />;
    case 'Weather Station':
      return <CloudIcon fontSize={fontSize} />;
    case 'Tank':
      return <WaterDropIcon fontSize={fontSize} />;
    case 'ConXenviro':
      return <AirIcon fontSize={fontSize} />;
    default:
      return <DevicesOtherIcon fontSize={fontSize} />;
  }
};

// BatteryLevel arrives as a string like "80%". Parse defensively.
export const batteryInfo = (level?: string): { pct: number | null; icon: React.ReactElement; color: string } => {
  const pct = level ? parseInt(level, 10) : NaN;
  if (Number.isNaN(pct)) {
    return { pct: null, icon: <BatteryAlertIcon fontSize="small" />, color: 'text.disabled' };
  }
  if (pct >= 90) return { pct, icon: <BatteryFullIcon fontSize="small" />, color: 'success.main' };
  if (pct >= 60) return { pct, icon: <Battery80Icon fontSize="small" />, color: 'success.main' };
  if (pct >= 40) return { pct, icon: <Battery50Icon fontSize="small" />, color: 'warning.main' };
  if (pct >= 15) return { pct, icon: <Battery20Icon fontSize="small" />, color: 'error.main' };
  return { pct, icon: <BatteryAlertIcon fontSize="small" />, color: 'error.main' };
};

// SignalStrengh arrives as a code like "Z9" / "G5" / "M3": network prefix
// (ZigBee / GSM / Mobile) followed by a 0-9 strength digit.
export const signalInfo = (code?: string): { label: string | null; icon: React.ReactElement; color: string } => {
  const clean = (code || '').trim();
  const match = /^([A-Za-z])(\d)$/.exec(clean);
  if (!match) {
    return { label: null, icon: <SignalOffIcon fontSize="small" />, color: 'text.disabled' };
  }
  const networks: Record<string, string> = { Z: 'ZigBee', G: 'GSM', M: 'Mobile' };
  const network = networks[match[1].toUpperCase()] || match[1].toUpperCase();
  const strength = parseInt(match[2], 10);
  const icon =
    strength >= 8 ? <Signal4Icon fontSize="small" /> :
    strength >= 6 ? <Signal3Icon fontSize="small" /> :
    strength >= 4 ? <Signal2Icon fontSize="small" /> :
    strength >= 1 ? <Signal1Icon fontSize="small" /> :
    <SignalOffIcon fontSize="small" />;
  const color = strength >= 6 ? 'success.main' : strength >= 4 ? 'warning.main' : strength >= 1 ? 'error.main' : 'text.disabled';
  return { label: `${network} ${strength}`, icon, color };
};

// Latest data transmission across the four channels (stored UTC).
export const lastSeen = (logger: Logger): Date | null => {
  const times = [logger.DataTime1, logger.DataTime2, logger.DataTime3, logger.DataTime4]
    .filter((v): v is string => !!v)
    .map((v) => parseUtc(v))
    .filter((d) => !Number.isNaN(d.getTime()));
  if (times.length === 0) return null;
  return new Date(Math.max(...times.map((d) => d.getTime())));
};

export const formatRelative = (date: Date | null): string | null => {
  if (!date) return null;
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return date.toLocaleDateString();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days <= 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString();
};

const FREQUENCY_LABELS: Record<string, string> = {
  ONE_MIN: '1 min intervals',
  FIVE_MIN: '5 min intervals',
  TEN_MIN: '10 min intervals',
  FIFTEEN_MIN: '15 min intervals',
  THIRTY_MIN: '30 min intervals',
  SIXTY_MIN: '60 min intervals',
  ONE_HOUR: '60 min intervals',
  ONE_HR: '1 hour intervals',
  SIX_HR: '6 hour intervals',
  TWELVE_HR: '12 hour intervals',
  TWENTY_FOUR_HR: '24 hour intervals',
};

/** Logging frequencies a user can pick, as TempTrack TimeInterval names. */
export const LOGGER_FREQUENCY_OPTIONS: { value: string; label: string }[] = [
  { value: 'ONE_MIN', label: '1 min' },
  { value: 'FIVE_MIN', label: '5 min' },
  { value: 'FIFTEEN_MIN', label: '15 min' },
  { value: 'THIRTY_MIN', label: '30 min' },
  { value: 'ONE_HR', label: '60 min' },
  { value: 'TWELVE_HR', label: '12 hours' },
  { value: 'TWENTY_FOUR_HR', label: '24 hours' },
];

// Minutes per stored frequency value, for the connection check.
const FREQUENCY_MINUTES: Record<string, number> = {
  ONE_MIN: 1, FIVE_MIN: 5, TEN_MIN: 10, FIFTEEN_MIN: 15, THIRTY_MIN: 30,
  SIXTY_MIN: 60, ONE_HOUR: 60, ONE_HR: 60, SIX_HR: 360, TWELVE_HR: 720, TWENTY_FOUR_HR: 1440,
};

/** ConXagent communication modes the user can switch to (TempTrack COMM_MODE).
 *  TempTrack has no Router mode. */
export const MODE_OPTIONS: { value: string; label: string; hint: string }[] = [
  { value: 'MOBILE', label: 'Mobile', hint: 'Sends over its own SIM (cellular).' },
  { value: 'GATEWAY', label: 'Gateway', hint: 'Sends over cellular and relays nearby XBee loggers.' },
  { value: 'ZIGBEE', label: 'XBee', hint: 'Sends through a nearby gateway or extend over XBee.' },
];

export const modeLabel = (mode?: string | null): string => {
  const clean = (mode || '').trim().toUpperCase();
  if (!clean || clean === 'NONE') return '—';
  if (clean === 'ZIGBEE') return 'XBee';
  if (clean === 'ROUTER') return 'Router';
  return clean.charAt(0) + clean.slice(1).toLowerCase();
};

/** Administrative LoggerInfo.Status codes, as the legacy app defines them. */
export const HARDWARE_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '5', label: 'In Use' },
  { value: '1', label: 'In Repair' },
  { value: '2', label: 'Single Use' },
  { value: '3', label: 'Retired' },
  { value: '4', label: 'Lost' },
];

export const hardwareStatusLabel = (code?: string | null): string =>
  HARDWARE_STATUS_OPTIONS.find((o) => o.value === (code || '').trim())?.label || '—';

// Which hardware types (LoggerInfo.Type) each capability applies to.
export const isConxagent = (logger?: Pick<Logger, 'Type'> | null): boolean => logger?.Type === '01';
export const hasLoggingFrequency = (logger?: Pick<Logger, 'Type'> | null): boolean =>
  ['01', '02', '03', '04', '10'].includes(logger?.Type || '');

/** The device's link to ConXedge. TempTrack's track status (UP / SUSPECT /
 *  DOWN) decides when known; otherwise the age of the last reading against
 *  the reporting interval: good within two intervals, late within a day. */
export const connectionState = (
  logger: Logger,
): { key: 'good' | 'late' | 'none'; label: string; color: 'success' | 'warning' | 'default' } => {
  switch ((logger.TrackStatus || '').toUpperCase()) {
    case 'UP':
      return { key: 'good', label: 'Connection to ConXedge good', color: 'success' };
    case 'SUSPECT':
      return { key: 'late', label: 'Connection to ConXedge late', color: 'warning' };
    case 'DOWN':
      return { key: 'none', label: 'No connection to ConXedge', color: 'default' };
    default:
      break;
  }
  const seen = lastSeen(logger);
  if (!seen) return { key: 'none', label: 'No contact yet', color: 'default' };
  const ageMin = (Date.now() - seen.getTime()) / 60000;
  const interval = FREQUENCY_MINUTES[logger.LoggerFrequency || ''] || 15;
  if (ageMin <= Math.max(interval * 2, 10)) return { key: 'good', label: 'Connection to ConXedge good', color: 'success' };
  if (ageMin <= 24 * 60) return { key: 'late', label: 'Connection to ConXedge late', color: 'warning' };
  return { key: 'none', label: 'No recent contact with ConXedge', color: 'default' };
};

// Product photos per TypeName, served from public/hardware. Types without a
// photo (or whose file fails to load) fall back to the type icon.
const HARDWARE_IMAGES: Record<string, string> = {
  Agent: 'conxagent.png',
  Sensor: 'conxsensor.png',
  'Digit Sensor': 'conxsensor.png',
  'Conx-1': 'conxone.png',
  Station: 'conxstation.png',
  Router: 'conxextend.png',
  Gateway: 'conxgateway.png',
  'Weather Station': 'weather-station.png',
  Tank: 'tank.png',
  ConXenviro: 'conxenviro.png',
};

export const hardwareImageUrl = (typeName?: string): string | null => {
  const file = typeName ? HARDWARE_IMAGES[typeName] : undefined;
  return file ? `${process.env.PUBLIC_URL || ''}/hardware/${file}` : null;
};

/** Product photo for a hardware type, or its icon when there is no photo. */
export const HardwareImage: React.FC<{ typeName?: string; size: number }> = ({ typeName, size }) => {
  const url = hardwareImageUrl(typeName);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [url]);
  if (!url || failed) {
    return (
      <Box
        component="span"
        sx={{
          width: size, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: `${Math.round(size / 6)}px`, bgcolor: 'action.hover', color: 'text.secondary', flexShrink: 0,
          '& .MuiSvgIcon-root': { fontSize: Math.max(18, Math.round(size * 0.55)) },
        }}
      >
        {typeIcon(typeName, 'small')}
      </Box>
    );
  }
  return (
    <img
      src={url}
      alt={typeName || 'hardware'}
      onError={() => setFailed(true)}
      style={{ width: size, height: size, objectFit: 'contain' }}
    />
  );
};

export const frequencyLabel = (freq?: string): string | null => {
  if (!freq || freq === 'None') return null;
  return FREQUENCY_LABELS[freq] || freq.replace(/_/g, ' ').toLowerCase();
};

export const formatDate = (value?: string): string => {
  if (!value) return '—';
  const d = parseUtc(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

// Hire, sold and manufactured dates are stored date-only (midnight), so
// read just the date part.
export const formatDateOnly = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

// Displays '—' for null-ish / placeholder backend values ("None", "NONE", "").
export const displayValue = (value?: string | null): string => {
  const clean = (value || '').trim();
  if (!clean || clean.toLowerCase() === 'none') return '—';
  return clean;
};

/** Who has the device, for the list's Client column and the device page:
 *  the buyer or hirer, or HDTECH with what the device is available for (so
 *  a hire unit never reads as HDTECH hiring it). */
export const assignmentInfo = (
  logger: Logger,
  // react-i18next's t (its result type is wider than string)
  translate: (...args: any[]) => any,
): { company: string; status?: string; color: 'success' | 'info' | 'error' | 'default'; withHdtech: boolean } => {
  const t = (...args: any[]): string => String(translate(...args));
  switch (logger.Assignment) {
    case 'ON_HIRE':
      return {
        company: logger.CompanyName || '—',
        withHdtech: false,
        color: logger.HireOverdue ? 'error' : 'info',
        status: logger.HireOverdue
          ? t('hardware.hireOverdueSince', 'Hire overdue since {{date}}', { date: formatDateOnly(logger.ReturnDate) })
          : logger.ReturnDate
            ? t('hardware.hiredUntil', 'Hired until {{date}}', { date: formatDateOnly(logger.ReturnDate) })
            : t('hardware.hired', 'Hired'),
      };
    case 'SOLD':
      return { company: logger.CompanyName || '—', withHdtech: false, color: 'success', status: t('hardware.sold', 'Sold') };
    case 'ASSIGNED':
      return { company: logger.CompanyName || '—', withHdtech: false, color: 'default' };
    default: {
      const purpose = (logger.SaleOrHire || '').toLowerCase();
      return {
        company: logger.CompanyName || 'HDTECH',
        withHdtech: true,
        color: 'default',
        status: purpose.includes('hire')
          ? t('hardware.availableForHire', 'Available for hire')
          : purpose.includes('sale')
            ? t('hardware.availableForSale', 'Available for sale')
            : undefined,
      };
    }
  }
};
